import React, { useEffect, useState } from 'react';
import { Table, Card, Button, Tag, Tooltip, message, Typography, Space, Popover } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../utils';
import { InfoCircleOutlined, ShareAltOutlined, EditOutlined, RocketOutlined, CalendarOutlined, DeleteOutlined } from '@ant-design/icons';
import { handleApiError } from '../utils/errorHandler';
import '../assets/css/tablesShadow.css';

const { Text } = Typography;

interface AudienceData {
    id: number;
    orgId: string;
    brandId: number;
    audienceId: number;
    campaignId?: number;
    previewImage?: string;
    campaignFields: string;
    status: 'Draft' | 'Initiating' | 'Creating' | 'Created' | 'Shared' | 'Sharing' | string;
    fields: string;
    createdAt: string;
    updatedAt: string;
    estimatedSize?: string;
    estimatedCost?: string;
    actualSize?: string;
    actualCost?: string;
}

const VerismartAudienceListing = () => {
    const [audiences, setAudiences] = useState<AudienceData[]>([]);
    const [brands, setBrands] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const pageSize = 10;

    // const formatAudienceSize = (size: number | undefined) => {
    //     if (size === undefined || size === null) return 'N/A';
    //     if (size < 0.001 && size > 0) return '< 100'; // for very small non-zero numbers
    //     if (size === 0) return '0';
    //     if (size < 1) return (size * 1000).toFixed(0) + " Thousand"; // e.g. 0.5 Million -> 500 Thousand
    //     return size.toFixed(2) + " Million";
    // };

    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg">Audience Campaign Management</div>,
            },
        ]);
    }, [setBreadcrumbs]);

    const fetchAudiences = async (page: number) => {
        setLoading(true);
        try {
            const response = await coreAPIService.get<{ meta: { status: boolean; code: number }; data: AudienceData[] }>(
                CTW_API_ENDPOINT,
                `v2/verismart/get-all-audiences?page=${page}&limit=${pageSize}`
            );

            if (response.meta.status && response.meta.code === 200) {
                setAudiences(response.data);
                setTotalCount(response.data.length); // Adjust if API provides total count
            } else {
                message.error('Failed to fetch audiences');
            }
        } catch (error) {
            handleApiError(error, 'verismart-audience-fetch', 'Failed to fetch audiences');
            message.error('Failed to fetch audiences');
        } finally {
            setLoading(false);
        }
    };

    const fetchBrands = async () => {
        try {
            const res = await coreAPIService.get<{ meta: { status: boolean, code: number }, data: any[] }>(
                CTW_API_ENDPOINT,
                "v2/verismart/get-all-brands"
            );
            if (res.meta.status && res.data) {
                setBrands(res.data);
            }
        } catch (error) {
            console.error('Error fetching brands:', error);
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    useEffect(() => {
        fetchAudiences(currentPage);
    }, [currentPage]);

    const getAudienceName = (fields: string | any) => {
        try {
            let parsedFields = fields;
            if (typeof fields === 'string') {
                parsedFields = JSON.parse(fields);
            }
            if (typeof parsedFields === 'string') {
                parsedFields = JSON.parse(parsedFields);
            }

            if (!Array.isArray(parsedFields)) return 'Unnamed Audience';

            const audienceGroupField = parsedFields.find((field: any) => 'audience_group' in field);
            return audienceGroupField?.audience_group?.value || 'Unnamed Audience';
        } catch (error) {
            handleApiError(error, 'audience-name-parse', 'Error parsing audience name');
            return 'Unnamed Audience';
        }
    };

    const getCampaignDetails = (campaignFieldsStr: string | any) => {
        try {
            if (!campaignFieldsStr || campaignFieldsStr === "null") return {};
            if (typeof campaignFieldsStr === 'string' && campaignFieldsStr.trim() === "") return {};

            let parsedFieldsArray = campaignFieldsStr;
            if (typeof campaignFieldsStr === 'string') {
                parsedFieldsArray = JSON.parse(campaignFieldsStr);
            }
            if (typeof parsedFieldsArray === 'string') {
                parsedFieldsArray = JSON.parse(parsedFieldsArray);
            }

            if (!Array.isArray(parsedFieldsArray)) return {};

            const details: { [key: string]: any } = {};

            parsedFieldsArray.forEach((field: any) => {
                if ('campaign_name' in field) details.name = field.campaign_name.value;
                if ('min_budget' in field) details.minBudget = field.min_budget.value;
                if ('max_budget' in field) details.maxBudget = field.max_budget.value;
                if ('start_duration' in field) details.startDate = new Date(field.start_duration.value);
                if ('end_duration' in field) details.endDate = new Date(field.end_duration.value);
            });

            return details;
        } catch (error) {
            handleApiError(error, 'campaign-fields-parse', 'Error parsing campaign fields');
            return { name: "Error parsing details" };
        }
    };

    const handleDelete = async (audienceId: number) => {
        try {
            const res = await coreAPIService.delete<any>(
                CTW_API_ENDPOINT,
                `v2/org/verismart-audience/${audienceId}`
            );
            if (res.meta.code === 200) {
                message.success('Audience deleted successfully');
                fetchAudiences(currentPage);
            } else {
                message.error('Failed to delete audience');
            }
        } catch (error) {
            handleApiError(error, 'audience-delete', 'Failed to delete audience');
            message.error('Failed to delete audience');
        }
    }

    // const getActionButtons = (record: AudienceData) => {
    //     const commonButtonProps = { style: { minWidth: '120px' } };

    //     // Column 1: Audience Creation Flow
    //     const audienceCreationButton = () => {
    //         switch (record.status) {
    //             case 'Draft':
    //                 return (
    //                     <>
    //                         <Button
    //                             {...commonButtonProps}
    //                             type="primary"
    //                             icon={<EditOutlined />}
    //                             onClick={() => {
    //                                 navigate(`/campaign/create?audienceId=${record.id}`, {
    //                                     state: {
    //                                         audienceFilters: record.fields,
    //                                         estimatedSize: record.estimatedSize,
    //                                         estimatedCpm: record.estimatedCost
    //                                     }
    //                                 });
    //                             }}
    //                         >
    //                             Create Audience
    //                         </Button>
    //                         <Popconfirm
    //                             title="Are you sure you want to delete this draft?"
    //                             onConfirm={async () => {
    //                                 try {
    //                                     const res = await coreAPIService.post<{ meta: { status: boolean, message: string } }>(
    //                                         CTW_API_ENDPOINT,
    //                                         `v2/verismart/delete-audience?audienceId=${record.audienceId}`,
    //                                         {}
    //                                     );
    //                                     if (res.meta.status) {
    //                                         message.success('Draft deleted successfully');
    //                                         fetchAudiences(currentPage);
    //                                     } else {
    //                                         message.error(res.meta.message || 'Failed to delete draft');
    //                                     }
    //                                 } catch (error) {
    //                                     message.error('Failed to delete draft');
    //                                 }
    //                             }}
    //                             okText="Yes"
    //                             cancelText="No"
    //                         >
    //                             <Button
    //                                 {...commonButtonProps}
    //                                 danger
    //                                 icon={<DeleteOutlined />}
    //                             >
    //                                 Delete
    //                             </Button>
    //                         </Popconfirm>
    //                     </>
    //                 );
    //             case 'Initiating':
    //             case 'Creating':
    //                 return (
    //                     <Button {...commonButtonProps} icon={<EditOutlined />} disabled>
    //                         {record.status === 'Creating' ? 'Creating...' : 'Processing'}
    //                     </Button>
    //                 );
    //             case 'Created':
    //             case 'Shared':
    //                 return (
    //                     <Button {...commonButtonProps} icon={<EditOutlined />} disabled>
    //                         Audience Created
    //                     </Button>
    //                 );
    //             default:
    //                 return null;
    //         }
    //     };

    //     // Column 2: Sharing Flow (only enabled if status is 'Created')
    //     const sharingButton = () => {
    //         const isEnabled = record.status === 'Created';

    //         return (
    //             <Button
    //                 {...commonButtonProps}
    //                 type={isEnabled ? "primary" : "default"}
    //                 icon={<ShareAltOutlined />}
    //                 style={{
    //                     ...commonButtonProps.style,
    //                     ...(isEnabled ? { borderColor: '#1890ff', color: '#1890ff' } : {})
    //                 }}
    //                 disabled={!isEnabled}
    //                 onClick={() => {
    //                     if (isEnabled) {
    //                         navigate(`/audience/${record.audienceId}/share-details`, { state: { record } });
    //                         message.info(`Sharing audience: ${getAudienceName(record.fields)}`);
    //                     }
    //                 }}
    //             >
    //                 Share Audience
    //             </Button>
    //         );
    //     };

    //     // Column 3: Campaign Creation (only enabled if status is 'Shared')
    //     const campaignButton = () => {
    //         const isEnabled = record.status === 'Shared';

    //         return (
    //             <Button
    //                 {...commonButtonProps}
    //                 type={isEnabled ? "primary" : "default"}
    //                 icon={<RocketOutlined />}
    //                 disabled={!isEnabled}
    //                 onClick={() => {
    //                     if (isEnabled) {
    //                         navigate('/campaign-management/create-campaign', {
    //                             state: {
    //                                 audienceFilters: record.fields,
    //                                 estimatedSize: record.estimatedSize,
    //                                 estimatedCpm: record.estimatedCost
    //                             }
    //                         });
    //                     }
    //                 }}
    //             >
    //                 Create Campaign
    //             </Button>
    //         );
    //     };

    //     return (
    //         <Space size="small">
    //             {audienceCreationButton()}
    //             {/* {sharingButton()} */}
    //             {campaignButton()}
    //         </Space>
    //     );
    // };

    const columns = [
        {
            title: 'ID',
            key: 'id',
            render: (record: AudienceData) => {
                return (
                    <div>{record.audienceId ? record.audienceId : "-"}</div>
                )
            },
            width: 50
        },
        {
            title: 'Name',
            key: 'audience',
            width: 200,
            render: (record: AudienceData) => {
                const audienceName = getAudienceName(record.fields).slice(0, 100);
                // console.log(record);

                const hasEstimated = record.estimatedSize !== undefined && record.estimatedCost !== undefined && record.estimatedSize?.length;
                // const hasActual = record.actualSize !== undefined && record.actualCost !== undefined;

                const metricsContent = (
                    <div>
                        {hasEstimated && (
                            <>
                                <Text style={{ display: 'block' }}>Est. Size: <Text strong>{Number(record.estimatedSize)?.toFixed(2)}M</Text></Text>
                                <Text style={{ display: 'block' }}>Est. CPM: <Text strong>₹{Number(record.estimatedCost)?.toFixed(2)}</Text></Text>
                            </>
                        )}
                        {/* {hasActual && (
                            <>
                                <Text style={{ display: 'block' }}>Size: <Text strong>{record.actualSize}M</Text></Text>
                                <Text style={{ display: 'block' }}>CPM: <Text strong>₹{record.actualCost}</Text></Text>
                            </>
                        )} */}
                        {!hasEstimated && (
                            <Text type="secondary">No metrics available</Text>
                        )}
                    </div>
                );

                return (
                    <div>
                        <Space>
                            <Text strong style={{ fontSize: '15px' }}>{audienceName}</Text>
                            <Popover
                                content={metricsContent}
                                title="Audience Metrics"
                                trigger="hover"
                                placement="right"
                            >
                                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                            </Popover>
                        </Space>
                    </div>
                );
            }
        },
        {
            title: 'Campaign Brief',
            key: 'campaignDetails',
            render: (record: AudienceData) => {
                const campaignDetails = getCampaignDetails(record.campaignFields);
                const imageUrl = record.previewImage?.replace(/`/g, '');

                if (!campaignDetails.name) {
                    return <Text type="secondary">No campaign details</Text>;
                }

                const gradientOverlay = imageUrl
                    ? 'linear-gradient(to bottom right, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.3) 100%)'
                    : 'none';

                const finalBackgroundImage = imageUrl
                    ? `${gradientOverlay}, url(${imageUrl})`
                    : 'none';

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {imageUrl && (
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '8px',
                                backgroundColor: '#333',
                                backgroundImage: finalBackgroundImage,
                                backgroundSize: 'cover, cover',
                                backgroundPosition: 'center, center',
                                flexShrink: 0,
                                overflow: 'hidden'
                            }} />
                        )}
                        <div>
                            <Text strong style={{ display: 'block' }}>{campaignDetails.name.length > 30 ? <Tooltip title={campaignDetails.name} > {campaignDetails.name.slice(0, 30) + "..."}</Tooltip> : campaignDetails.name}</Text>
                            {(campaignDetails.minBudget || campaignDetails.maxBudget) && (
                                <Text type="secondary" style={{ display: 'block' }}>
                                    Budget: ₹{campaignDetails.minBudget?.toLocaleString() || 0}
                                    {campaignDetails.maxBudget !== campaignDetails.minBudget &&
                                        ` - ₹${campaignDetails.maxBudget?.toLocaleString()}`}
                                </Text>
                            )}
                            {(campaignDetails.startDate && campaignDetails.endDate) && (
                                <Text type="secondary" style={{ display: 'block' }}>
                                    <CalendarOutlined style={{ marginRight: '4px' }} />
                                    {campaignDetails.startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} -
                                    {campaignDetails.endDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </Text>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Brand',
            key: 'brand',
            width: 150,
            render: (record: AudienceData) => {
                const brand = brands.find(b => b.brandId === record.brandId || b.brandDbId === record.brandId);
                return brand ? brand.brandName : '-';
            }
        },
        {
            title: 'Metrics',
            dataIndex: 'actualSize', // or keep metrics, doesn't matter
            key: 'metrics',
            render: (_: any, record: AudienceData) => {
                return record?.actualSize ? (
                    <>
                        <Text style={{ display: 'block' }}>
                            Size: <Text strong>{record.actualSize}</Text>
                        </Text>
                        <Text style={{ display: 'block' }}>
                            CPM: <Text strong>₹{record.actualCost}</Text>
                        </Text>
                    </>
                ) : (
                    "-"
                );
            },
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: (date: string) => {
                return (
                    <Tooltip title={new Date(date).toLocaleString()}>
                        {new Date(date).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric'
                        })}
                    </Tooltip>
                );
            }
        },
        // {
        //     title: 'Audience Status',
        //     key: 'status',
        //     width: 150,
        //     render: (record: AudienceData) => {
        //         const statusColors = {
        //             'Draft': 'gold',
        //             'Initiating': 'blue',
        //             'Created': 'green',
        //             'Shared': 'purple',
        //             'default': 'default'
        //         };

        //         return (

        //             record.status === "Draft" ? <Button
        //                 type="primary"
        //                 size="middle"
        //                 icon={<EditOutlined />}
        //                 onClick={() => {
        //                     localStorage.setItem('formattedFilters', record.fields);
        //                     navigate(`/campaign/create?audienceId=${record.id}`);
        //                 }}

        //                 className='w-full bg-blue-500'
        //             >
        //                 Create Audience
        //             </Button> : <Tag color={statusColors[record.status as keyof typeof statusColors] || statusColors.default}
        //                 style={{ width: '100%', textAlign: 'center', padding: '4px 0', fontSize: '14px' }}>
        //                 {record.status}
        //             </Tag>
        //         )
        //     }
        // },
        {
            title: 'Status',
            key: 'statusAction',
            width: 200,
            render: (record: AudienceData) => {
                // Define status colors

                // Get appropriate action button based on status
                let actionButton = null;
                // const statusColors = {
                //     'Draft': 'gold',
                //     'Initiating': 'blue',
                //     'Created': 'green',
                //     'Shared': 'purple',
                //     'default': 'default'
                // };

                switch (record.status) {
                    case 'Draft':
                        actionButton = (
                            <>
                                <Button
                                    type="primary"
                                    size="middle"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        navigate(`/campaign/create?audienceId=${record.id}`, {
                                            state: {
                                                audienceFilters: record.fields,
                                                estimatedSize: record.estimatedSize,
                                                estimatedCpm: record.estimatedCost
                                            }
                                        });
                                    }}

                                    className='w-full bg-blue-500'
                                >
                                    Create Audience
                                </Button>
                                {/* <Button
                                    type="dashed"
                                    size="middle"
                                    icon={<ShareAltOutlined />}
                                    onClick={() => {
                                        navigate(`/audience/${record.audienceId}/share-details`, { state: { record } });
                                        message.info(`Sharing audience: ${getAudienceName(record.fields)}`);
                                    }}
                                    className='w-full bg-blue-500'
                                    disabled
                                >
                                    Share Audience
                                </Button> */}
                                <Button
                                    type="dashed"
                                    size="middle"
                                    icon={<RocketOutlined />}
                                    onClick={() => navigate('/campaign-management/create-campaign', {
                                        state: {
                                            audienceId: record.audienceId,
                                            audienceName: getAudienceName(record.fields),
                                            campaignData: record
                                        }
                                    })}
                                    className='w-full bg-blue-600'
                                    disabled

                                >
                                    Create Campaign
                                </Button>
                            </>
                        );
                        break;
                    case 'Initiating':
                    case 'Creating':
                        actionButton = (
                            <Button
                                type="default"
                                size="middle"
                                disabled
                                className='w-full'

                            >
                                {record.status === 'Creating' ? 'Creating...' : 'Processing'}
                            </Button>
                        );
                        break;
                    case 'Sharing':
                        actionButton = (
                            <Button
                                type="default"
                                size="middle"
                                disabled
                                className='w-full'

                            >
                                Sharing InProgress
                            </Button>
                        );
                        break;
                    case 'Created':
                        actionButton = (
                            <>
                                <Tag color="green"
                                    style={{ width: '100%', textAlign: 'center', padding: '4px 0', fontSize: '14px' }}>
                                    Audience Created
                                </Tag>
                                <Button
                                    type="primary"
                                    size="middle"
                                    icon={<ShareAltOutlined />}
                                    onClick={async () => {
                                        const res = await coreAPIService.post<{ meta: { status: boolean } }>(CTW_API_ENDPOINT, `/v2/verismart/share-audience?audienceId=${record.audienceId}`)
                                        if (res.meta.status) {
                                            message.success("Audience Shared Successfully")
                                            fetchAudiences(1)
                                        }
                                    }}
                                    className='w-full bg-blue-500'

                                >
                                    Share Audience
                                </Button>
                                <Button
                                    type="dashed"
                                    size="middle"
                                    icon={<RocketOutlined />}
                                    onClick={() => navigate('/campaign-management/create-campaign', {
                                        state: {
                                            audienceId: record.audienceId,
                                            audienceName: getAudienceName(record.fields),
                                            campaignData: record
                                        }
                                    })}
                                    className='w-full bg-blue-600'
                                    disabled

                                >
                                    Create Campaign
                                </Button>
                            </>
                        );
                        break;
                    case 'Shared':
                        actionButton = (
                            <>
                                <Tag color="green"
                                    style={{ width: '100%', textAlign: 'center', padding: '4px 0', fontSize: '14px' }}>
                                    Audience Shared
                                </Tag>
                                {/* <Tag color="green"
                                    style={{ width: '100%', textAlign: 'center', padding: '4px 0', fontSize: '14px' }}>
                                    Audience Shared
                                </Tag> */}
                                <Button
                                    type="primary"
                                    size="middle"
                                    icon={<RocketOutlined />}
                                    onClick={() => navigate('/campaign-management/create-campaign', {
                                        state: {
                                            audienceId: record.audienceId,
                                            audienceName: getAudienceName(record.fields),
                                            campaignData: record
                                        }
                                    })}
                                    className='w-full bg-blue-600'

                                >
                                    Create Campaign
                                </Button>
                            </>
                        );
                        break;
                    default:
                        actionButton = (
                            <Button type="default" size="middle" disabled>
                                Creating Audience
                            </Button>
                        );
                }

                return (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>

                        {actionButton}
                    </Space>
                );
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (record: AudienceData) => {
                return (
                    <div>
                        {record.audienceId === null && <DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} onClick={() => handleDelete(record.id)} />}
                    </div>
                )
            }
        }
    ];

    return (
        <div className="px-6 py-4 md:px-10">
            <Card
                title={<Text style={{ fontSize: '20px', fontWeight: 600 }}>Audience Dashboard</Text>}
                className='shadow-lg rounded-lg p-4'
                bordered={false}
            >
                <div className='bg-gray-50 p-4'>
                    <Table
                        columns={columns}
                        dataSource={audiences}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            total: totalCount,
                            pageSize,
                            current: currentPage,
                            onChange: (page) => setCurrentPage(page),
                            showSizeChanger: false,
                            align: "center"
                        }}
                        scroll={{ x: 'max-content' }}
                        className="verismart-audience-table"
                        style={{ marginTop: '16px' }}
                    />
                </div>
            </Card>
        </div>
    );
};

export default VerismartAudienceListing;

// You might want to add some CSS for .verismart-audience-table if needed,
// for example, in your global CSS file or a component-specific CSS file:
/*
.verismart-audience-table .ant-table-thead > tr > th {
  background-color: #f0f2f5; // Light grey header
  font-weight: 600;
}
.verismart-audience-table .ant-table-tbody > tr > td {
  vertical-align: middle;
}
.verismart-audience-table .ant-tag {
  min-width: 70px;
  text-align: center;
}
*/